import {
  ArrowRight,
  CheckCircle2,
  Cloud,
  Download,
  ImagePlus,
  MessageSquareQuote,
  Mic2,
  PencilLine,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link, type MetaFunction } from "react-router";

import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";

import handwritingExample from "./assets/handwriting-example.png";

export const meta: MetaFunction = () => {
  return [
    { title: publicEnv.siteTitle },
    {
      name: "description",
      content:
        "Help your child practise handwriting with family photos, and keep their words and changing letter shapes through the years.",
    },
  ];
};

// Introduce the one product job: practising handwriting with a personal story
// and preserving the page as a memory.
//
export default function Marketing() {
  return (
    <>
      <section className="grid items-center gap-12 py-14 lg:grid-cols-[1.02fr_0.98fr] lg:py-24">
        <div className="flex flex-col items-start gap-6">
          <Badge className="bg-accent text-accent-foreground" variant="outline">
            <PencilLine /> Their words. Their handwriting. Their growing up.
          </Badge>
          <h1 className="font-display max-w-3xl text-5xl leading-[0.98] font-semibold tracking-[-0.045em] sm:text-7xl">
            Practise writing today.
            <br />
            Keep a piece of childhood.
          </h1>
          <p className="text-muted-foreground max-w-2xl text-lg leading-8">
            Start with a family photo. Let your child tell its story, then write
            it on paper. Anecdote keeps their words and original handwritten
            marks, so you can see how they change as your child grows.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button asChild size="lg">
              <Link to="/login">
                Start with one photo <ArrowRight />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href="#how-it-works">See how it works</a>
            </Button>
          </div>
          <p className="text-muted-foreground text-sm">
            A photo, paper, and a pencil are enough. Save unfinished pages and
            come back whenever your child is ready.
          </p>
        </div>

        <StoryMockup />
      </section>

      <section className="border-y py-16" id="how-it-works">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-primary text-sm font-medium">A small ritual</p>
          <h2 className="font-display mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">
            A story to tell. A reason to write. A page to keep.
          </h2>
        </div>

        <div className="mt-12 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Step icon={ImagePlus} number="01" title="Choose and send">
            A guardian selects a photo from this device or the Dropbox Chooser,
            adds an optional question, and chooses the child who can see it.
          </Step>
          <Step icon={Mic2} number="02" title="Let them tell it">
            Say the story aloud or type it, then choose a sentence to write.
            Grammar suggestions are optional. Their own wording matters.
          </Step>
          <Step icon={PencilLine} number="03" title="Write it on paper">
            Give handwriting its own moment. Use ordinary paper or print a plain
            or ruled sheet, then photograph the page and choose the crop.
          </Step>
          <Step
            icon={CheckCircle2}
            number="04"
            title="Keep their original marks"
          >
            Review the original crop and print copy together. A guardian
            approves the page, and it joins the family’s handwriting history.
          </Step>
        </div>
      </section>

      <section className="grid gap-10 py-20 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
        <div>
          <p className="text-primary text-sm font-medium">
            Growing up in their own handwriting
          </p>
          <h2 className="font-display mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">
            Those letters will never look quite like this again.
          </h2>
          <p className="text-muted-foreground mt-5 max-w-xl leading-7">
            A wobbly letter, an unexpected spelling, a longer story a year
            later. Keep each page, return to the same photograph, and compare
            your child’s handwriting across different moments.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <ValueCard icon={PencilLine} title="Writing is the activity">
            Every finished story includes handwriting. A sentence is enough to
            start, and unfinished work can wait for another day.
          </ValueCard>
          <ValueCard icon={Mic2} title="Their words stay theirs">
            Speaking, grammar help, and handwriting recognition are optional.
            Keep the original wording and spelling, even when a helper suggests
            something different.
          </ValueCard>
          <ValueCard icon={UserRound} title="A focused kid mode">
            Children see only photos sent to their profile, while guardian
            controls stay behind an adult check.
          </ValueCard>
          <ValueCard icon={Download} title="Keep every stage of growing up">
            Compare dated pages, revisit a memory, and print one story per page.
            Reading and downloads stay available after cancellation.
          </ValueCard>
        </div>
      </section>

      <section className="bg-accent/60 grid gap-10 rounded-3xl border px-6 py-12 sm:px-10 lg:grid-cols-[0.9fr_1.1fr] lg:px-14">
        <div>
          <ShieldCheck className="text-primary size-7" />
          <h2 className="font-display mt-5 text-4xl font-semibold tracking-tight">
            Private family memories, in your hands.
          </h2>
        </div>
        <div className="grid gap-5">
          <PrivacyPoint icon={Cloud} title="Dropbox stays a chooser">
            Anecdote FYI does not connect to or browse your Dropbox account. The
            provider sends only the files you explicitly select in its Chooser.
          </PrivacyPoint>
          <PrivacyPoint icon={ShieldCheck} title="You choose what is kept">
            Crop the handwriting photograph before saving. We keep that
            metadata-free crop with a separate print copy, and you can download
            both in ordinary image files. Audio is never saved.
          </PrivacyPoint>
          <PrivacyPoint icon={UserRound} title="Access follows the child">
            A selected photo is available only to guardians and the child
            profiles the guardian chose for that prompt.
          </PrivacyPoint>
        </div>
      </section>

      <section className="bg-ink text-ink-foreground anecdote-thread my-16 overflow-hidden rounded-[2rem] px-6 py-14 text-center sm:px-12 sm:py-20">
        <MessageSquareQuote className="text-ink-accent mx-auto size-8" />
        <h2 className="font-display mx-auto mt-6 max-w-3xl text-4xl font-semibold tracking-tight sm:text-5xl">
          The handwriting they make today is a memory you can keep.
        </h2>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button
            asChild
            className="bg-ink-foreground text-ink hover:bg-ink-foreground/90"
            size="lg"
          >
            <Link to="/login">Start a handwritten story</Link>
          </Button>
          <Button
            asChild
            className="border-ink-muted text-ink-foreground hover:bg-ink-surface hover:text-ink-foreground"
            size="lg"
            variant="outline"
          >
            <Link to="/pricing">View pricing</Link>
          </Button>
        </div>
      </section>
    </>
  );
}

// A labeled fictional example makes the handwriting visible before signup.
function StoryMockup() {
  return (
    <figure className="bg-card mx-auto w-full max-w-xl overflow-hidden rounded-[2rem] border shadow-2xl shadow-slate-950/10">
      <img
        alt="Two illustrative handwritten stories about a frog: large early letters beside a longer, more confident handwritten sentence"
        className="w-full"
        fetchPriority="high"
        height={1024}
        src={handwritingExample}
        width={1536}
      />
      <figcaption className="grid gap-2 p-5">
        <p className="font-serif text-2xl">
          One memory. Two moments in growing up.
        </p>
        <p className="text-muted-foreground text-sm">
          Keep the first page and the next one. Compare the marks, the words,
          and the story.
        </p>
        <p className="text-muted-foreground text-xs">
          AI-generated illustrative example · fictional handwriting
        </p>
      </figcaption>
    </figure>
  );
}

// Explain one numbered step in the guardian-to-child story workflow.
//
function Step({
  children,
  icon: Icon,
  number,
  title,
}: {
  children: ReactNode;
  icon: typeof ImagePlus;
  number: string;
  title: string;
}) {
  return (
    <article className="bg-card rounded-2xl border p-6">
      <div className="flex items-center justify-between">
        <span className="bg-accent text-accent-foreground grid size-11 place-items-center rounded-xl">
          <Icon className="size-5" />
        </span>
        <span className="text-muted-foreground font-mono text-xs">
          {number}
        </span>
      </div>
      <h3 className="font-display mt-6 text-2xl font-semibold">{title}</h3>
      <p className="text-muted-foreground mt-3 leading-7">{children}</p>
    </article>
  );
}

// Summarize one way the child keeps authorship of the response.
//
function ValueCard({
  children,
  icon: Icon,
  title,
}: {
  children: ReactNode;
  icon: typeof Mic2;
  title: string;
}) {
  return (
    <article className="bg-card rounded-2xl border p-5">
      <Icon className="text-primary size-5" />
      <h3 className="mt-4 font-semibold">{title}</h3>
      <p className="text-muted-foreground mt-2 text-sm leading-6">{children}</p>
    </article>
  );
}

// State one concrete boundary for photo selection, storage, or access.
//
function PrivacyPoint({
  children,
  icon: Icon,
  title,
}: {
  children: ReactNode;
  icon: typeof Cloud;
  title: string;
}) {
  return (
    <article className="flex gap-4">
      <span className="bg-background grid size-10 shrink-0 place-items-center rounded-xl border">
        <Icon className="text-primary size-4" />
      </span>
      <div>
        <h3 className="font-medium">{title}</h3>
        <p className="text-muted-foreground mt-1 text-sm leading-6">
          {children}
        </p>
      </div>
    </article>
  );
}
